import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import {
  Badge,
  Col,
  Row,
  Space,
  Spin,
  Upload,
  notification,
  Progress,
  Tooltip,
  Drawer,
  Empty,
} from "antd";
import { formatDateTime } from "../../utils/helpers";
import {
  ThemeButton,
  ThemeDivider,
  ThemeFilterBtn,
  ThemeInput,
  ThemeMainWrapper,
  ThemeModal,
  ThemeRadio,
  ThemeTag,
  ThemeUploadDragDrop,
} from "../Styles/Theme";
import {
  CardDropdown,
  ProjectChatCard,
  ProjectChatSessionsDetailsCard,
  ProjectChatSessionsTag,
  ProjectDetailsCard,
  ProjectDetailsUploadCard,
  ProjectMainDetails,
  ProjectSettingsCard,
} from "./ProjectsStyle";
import {
  MoreOutlined,
  RightOutlined,
  DeleteOutlined,
  SyncOutlined,
  ReloadOutlined,
  ShareAltOutlined,
  SettingOutlined,
  CheckOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeTable } from '../Styles/MasterStyle';
import MobHeader from '../../Components/Header/MobHeader';
import {
  getProjectByIdApi,
  getProjectByIdReset,
  getProjectFilesApi,
  getProjectFilesReset,
  uploadProjectFileApi,
  uploadProjectFileReset,
  deleteProjectFileApi,
  deleteProjectFileReset,
  downloadProjectFileApi,
  uploadFilesToCloud,
  bulkUploadReset,
  getIndexingStatusApi,
  reindexFilesApi,
  updateProjectSettingsApi,
  updateProjectSettingsReset,
  updateProjectApi,
  regenerateProjectContextApi,
  getEligibleStudiesForImportApi,
  getEligibleStudiesReset,
  importFromStudiesApi,
  importFromStudiesReset,
  getProjectHistoryApi,
} from "../../store/actions/projectAction";
import {
  createChatApi,
  createChatReset,
  getChatsByProjectApi,
  getChatsByProjectReset,
  deleteChatApi,
  deleteChatReset,
} from "../../store/actions/chatAction";
import { Input, Radio, Select } from "antd";
import ShareModal from "../../Components/ShareModal/ShareModal";
import EditableTitle from "../../Components/common/EditableTitle";
import EditableDescription from "../../Components/common/EditableDescription";
import { useProjectPermissions, getUserId, getUserType, USER_TYPES } from "../../utils/permissions";
import { validateDocumentFile } from "../../utils/fileValidation";
import { isProjectSharedToMe, hasSharePermission } from "../../utils/sharePermissions";
import Lottie from "lottie-react";
import loaderJson from "../../jsons/upload.json";

const { TextArea } = Input;

const CrowdPulseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 8, verticalAlign: 'middle' }}>
    <path d="M2 12h4l2-6 4 12 3-8 2 4h5" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CrowdLensIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 8, verticalAlign: 'middle' }}>
    <circle cx="12" cy="12" r="9" stroke="#10b981" strokeWidth="2"/>
    <circle cx="12" cy="12" r="5" stroke="#10b981" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="2" fill="#10b981"/>
  </svg>
);

const AI_PROVIDER_OPTIONS = {
  openai: {
    key: 'crowdpulse',
    providerKey: 'openai',
    name: 'Qualitative Analysis Engine',
    icon: CrowdPulseIcon,
    description: 'Where voices become vision',
  },
  gemini: {
    key: 'crowdlens',
    providerKey: 'gemini',
    name: 'Quantitative Analysis Engine',
    icon: CrowdLensIcon,
    description: 'See beyond the surface',
  },
};

const EllipsisChip = ({ text, style, icon }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [text]);

  const chip = icon ? (
    <span style={style}>
      {icon}
      <span ref={textRef} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {text}
      </span>
    </span>
  ) : (
    <span ref={textRef} style={style}>
      {text}
    </span>
  );

  if (!isTruncated) return chip;

  return (
    <Tooltip
      title={text}
      overlayInnerStyle={{
        fontSize: 12,
        padding: '4px 10px',
        minHeight: 'auto',
        borderRadius: 6,
      }}
    >
      {chip}
    </Tooltip>
  );
};

const ProjectDetails = (props) => {
  const {
    getProjectById,
    getProjectByIdReset,
    getProjectByIdState,
    getProjectFiles,
    getProjectFilesReset,
    getProjectFilesState,
    uploadProjectFile,
    uploadProjectFileReset,
    uploadProjectFileState,
    deleteProjectFile,
    deleteProjectFileReset,
    deleteProjectFileState,
    downloadProjectFile,
    uploadFilesToCloud,
    bulkUploadReset,
    bulkUploadState,
    getIndexingStatus,
    reindexFiles,
    regenerateProjectContext,
    updateProjectSettings,
    updateProjectSettingsReset,
    updateProjectSettingsState,
    updateProject,
    createChat,
    createChatReset,
    createChatState,
    getChatsByProject,
    getChatsByProjectReset,
    getChatsByProjectState,
    deleteChat,
    deleteChatReset,
    deleteChatState,
    getEligibleStudies,
    getEligibleStudiesReset,
    getEligibleStudiesState,
    importFromStudies,
    importFromStudiesReset,
    importFromStudiesState,
  } = props;

  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);

  // Build back URL preserving folder context
  const getBackUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const fromFolderId = params.get('fromFolderId');
    const fromFolderName = params.get('fromFolderName');
    if (fromFolderId) {
      return `/projects/?folderId=${fromFolderId}&folderName=${encodeURIComponent(fromFolderName || '')}&page=1`;
    }
    return '/projects/';
  };
  
  // Permission checks
  const { can, isSuperAdmin } = useProjectPermissions();
  const currentUserId = getUserId();
  const currentUserType = getUserType();

  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [indexingStatus, setIndexingStatus] = useState(null);
  const [isReindexing, setIsReindexing] = useState(false);
  const previousIndexingStatusRef = useRef(null);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [defaultProvider, setDefaultProvider] = useState("openai");
  const [demographicFormat, setDemographicFormat] = useState("anonymized");
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isSavingDemographicFormat, setIsSavingDemographicFormat] = useState(false);
  const [isRegeneratingContext, setIsRegeneratingContext] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [newSessionModal, setNewSessionModal] = useState(false);
  const [newSessionName, setNewSessionName] = useState("");
  const [newSessionProvider, setNewSessionProvider] = useState("");
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [uploadModalTab, setUploadModalTab] = useState('upload'); // 'upload' or 'import'
  const [eligibleStudies, setEligibleStudies] = useState([]);
  const [selectedStudyIds, setSelectedStudyIds] = useState([]);
  const [studySearchTerm, setStudySearchTerm] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [batchTag, setBatchTag] = useState(''); // Tag for batch uploads/imports
  const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false);
  const [projectHistory, setProjectHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [expandedBatches, setExpandedBatches] = useState({});
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  const fetchIndexingStatus = async () => {
    try {
      const response = await getIndexingStatus(id);
      if (response?.data) {
        setIndexingStatus(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch indexing status:", error);
    }
  };

  const fetchProjectHistory = async (page = 1) => {
    if (page === 1) {
      setIsLoadingHistory(true);
    } else {
      setIsLoadingMore(true);
    }
    try {
      const response = await getProjectHistoryApi(id, page);
      if (response?.data?.history) {
        if (page === 1) {
          setProjectHistory(response.data.history);
        } else {
          // Merge new date groups into existing ones (immutable)
          setProjectHistory(prev => {
            const merged = prev.map(g => ({ ...g, items: [...g.items] }));
            for (const newGroup of response.data.history) {
              const existing = merged.find(g => g.date === newGroup.date);
              if (existing) {
                existing.items.push(...newGroup.items);
              } else {
                merged.push({ ...newGroup });
              }
            }
            return merged;
          });
        }
        setHistoryHasMore(response.data.hasMore);
        setHistoryPage(page);
      }
    } catch (error) {
      console.error("Failed to fetch project history:", error);
    } finally {
      setIsLoadingHistory(false);
      setIsLoadingMore(false);
    }
  };

  const openHistoryDrawer = () => {
    setHistoryDrawerVisible(true);
    setHistoryPage(1);
    setHistoryHasMore(false);
    setExpandedBatches({});
    fetchProjectHistory(1);
  };

  useEffect(() => {
    if (id) {
      getProjectById(id);
      getProjectFiles(id);
      fetchIndexingStatus();
      getChatsByProject(id);
    }
    return () => {
      getProjectByIdReset();
      getProjectFilesReset();
      getChatsByProjectReset();
    };
  }, [id]);

  useEffect(() => {
    let pollingInterval;
    if (indexingStatus?.projectStatus === "indexing") {
      pollingInterval = setInterval(() => {
        fetchIndexingStatus();
        getProjectFiles(id);
      }, 5000);
    }
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [indexingStatus?.projectStatus, id]);

  // Detect when indexing completes (status changes from "indexing" to "ready")
  // and refresh project data to update vector_stores status for the dropdown
  useEffect(() => {
    const previousStatus = previousIndexingStatusRef.current;
    const currentStatus = indexingStatus?.projectStatus;

    // If status changed from "indexing" to "ready", refresh project data
    if (previousStatus === "indexing" && currentStatus === "ready") {
      getProjectById(id);
    }

    // Update the ref with current status
    previousIndexingStatusRef.current = currentStatus;
  }, [indexingStatus?.projectStatus, id, getProjectById]);

  useEffect(() => {
    if (getProjectByIdState.apiState === "success") {
      const projectData = getProjectByIdState.data?.data || null;
      setProject(projectData);
      if (projectData) {
        setSystemPrompt(projectData.system_prompt || "");
        setDefaultProvider(projectData.default_provider || "openai");
        setDemographicFormat(projectData.demographic_format || "anonymized");
      }
      setLoading(false);
    } else if (getProjectByIdState.apiState === "error") {
      notification.error({
        message: "Error",
        description: getProjectByIdState.message || "Failed to fetch project details",
      });
      setLoading(false);
    } else if (getProjectByIdState.apiState === "loading") {
      setLoading(true);
    }
  }, [getProjectByIdState]);

  useEffect(() => {
    if (getProjectFilesState.apiState === "success") {
      setFiles(getProjectFilesState.data?.data?.files || []);
    } else if (getProjectFilesState.apiState === "error") {
      notification.error({
        message: "Error",
        description: getProjectFilesState.message || "Failed to fetch files",
      });
    }
  }, [getProjectFilesState]);

  useEffect(() => {
    if (uploadProjectFileState.apiState === "success") {
      notification.success({
        message: "Success",
        description: "File uploaded successfully",
      });
      setUploadModal(false);
      setIsUploading(false);
      setSelectedFiles([]);
      setBatchTag('');
      getProjectFiles(id);
      getProjectById(id);
      fetchIndexingStatus();
      uploadProjectFileReset();
    } else if (uploadProjectFileState.apiState === "error") {
      notification.error({
        message: "Error",
        description: uploadProjectFileState.message || "Failed to upload file",
      });
      setIsUploading(false);
      uploadProjectFileReset();
    }
  }, [uploadProjectFileState]);

  useEffect(() => {
    if (bulkUploadState.apiState === "success") {
      const { successful, failed } = bulkUploadState.data?.data || {};
      notification.success({
        message: "Upload Complete",
        description: `${successful || 0} file(s) uploaded successfully${failed ? `, ${failed} failed` : ""}`,
      });
      setUploadModal(false);
      setIsUploading(false);
      setSelectedFiles([]);
      setBatchTag('');
      getProjectFiles(id);
      getProjectById(id);
      fetchIndexingStatus();
      bulkUploadReset();
    } else if (bulkUploadState.apiState === "error") {
      notification.error({
        message: "Error",
        description: bulkUploadState.message || "Failed to upload files",
      });
      setIsUploading(false);
      bulkUploadReset();
    }
  }, [bulkUploadState.apiState]);

  useEffect(() => {
    if (deleteProjectFileState.apiState === "success") {
      notification.success({
        message: "File Deleted",
        description: "File has been deleted successfully.",
      });
      getProjectFiles(id);
      getProjectById(id);
      fetchIndexingStatus();
      deleteProjectFileReset();
    } else if (deleteProjectFileState.apiState === "error") {
      notification.error({
        message: "Error",
        description: deleteProjectFileState.message || "Failed to delete file",
      });
      deleteProjectFileReset();
    }
  }, [deleteProjectFileState]);

  useEffect(() => {
    if (updateProjectSettingsState.apiState === "success") {
      notification.success({
        message: "Settings Saved",
        description: "Project settings have been updated successfully.",
      });
      setIsSavingSettings(false);
      setIsSavingDemographicFormat(false);
      getProjectById(id);
      updateProjectSettingsReset();
    } else if (updateProjectSettingsState.apiState === "error") {
      notification.error({
        message: "Error",
        description: updateProjectSettingsState.message || "Failed to save settings",
      });
      setIsSavingSettings(false);
      setIsSavingDemographicFormat(false);
      updateProjectSettingsReset();
    }
  }, [updateProjectSettingsState]);

  useEffect(() => {
    if (getChatsByProjectState.apiState === "success") {
      setChatSessions(getChatsByProjectState.data?.data?.chats || []);
    }
  }, [getChatsByProjectState]);

  useEffect(() => {
    if (createChatState.apiState === 'success') {
      const chatId = createChatState.data?.data?._id;
      setIsCreatingChat(false);
      setNewSessionModal(false);
      setNewSessionName('');
      setNewSessionProvider('');
      createChatReset();
      if (chatId) {
        navigate(`/insight-engine/${chatId}`);
      }
    } else if (createChatState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: createChatState.message || 'Failed to create chat session',
      });
      setIsCreatingChat(false);
      createChatReset();
    }
  }, [createChatState]);

  useEffect(() => {
    if (deleteChatState.apiState === 'success') {
      notification.success({
        message: 'Session Deleted',
        description: 'Chat session has been deleted successfully.',
      });
      getChatsByProject(id);
      deleteChatReset();
    } else if (deleteChatState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: deleteChatState.message || 'Failed to delete session',
      });
      deleteChatReset();
    }
  }, [deleteChatState]);

  useEffect(() => {
    if (getEligibleStudiesState.apiState === 'success') {
      setEligibleStudies(getEligibleStudiesState.data?.data?.studies || []);
    } else if (getEligibleStudiesState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getEligibleStudiesState.message || 'Failed to fetch eligible studies',
      });
    }
  }, [getEligibleStudiesState]);

  useEffect(() => {
    if (importFromStudiesState.apiState === 'success') {
      const responseData = importFromStudiesState.data?.data || {};
      const responseMessage = importFromStudiesState.data?.message;
      const { imported, skipped, failed } = responseData;

      // Show appropriate notification based on results
      if (failed > 0 && imported === 0) {
        notification.error({
          message: 'Import Failed',
          description: responseMessage || `All ${failed} transcripts failed to import.`,
        });
      } else if (failed > 0) {
        notification.warning({
          message: 'Import Partially Complete',
          description: responseMessage,
        });
      } else {
        notification.success({
          message: 'Import Complete',
          description: responseMessage || `${imported || 0} transcripts imported.`,
        });
      }

      setUploadModal(false);
      setIsImporting(false);
      setSelectedStudyIds([]);
      setBatchTag('');
      setUploadModalTab('upload');
      getProjectFiles(id);
      getProjectById(id);
      fetchIndexingStatus();
      importFromStudiesReset();
    } else if (importFromStudiesState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: importFromStudiesState.message || 'Failed to import transcripts',
      });
      setIsImporting(false);
      importFromStudiesReset();
    }
  }, [importFromStudiesState]);

  const handleFilesSelected = fileList => {
    const newFiles = Array.from(fileList);
    const validFiles = [];
    const errors = [];

    // Validate each file BEFORE adding to state
    newFiles.forEach(file => {
      const result = validateDocumentFile(file);
      if (result.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${result.error}`);
      }
    });

    // Show error notification for invalid files
    if (errors.length > 0) {
      notification.error({
        message: 'Invalid File(s)',
        description: errors.join('\n'),
        duration: 5,
      });
    }

    // Only add valid files to state
    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) {
      notification.warning({
        message: 'No Files',
        description: 'Please select files to upload',
      });
      return;
    }

    setIsUploading(true);
    try {
      await uploadFilesToCloud(id, selectedFiles, { batchTag: batchTag.trim() || '' });
    } catch (error) {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = (fileId, fileName) => {
    setFileToDelete({ fileId, fileName });
    setDeleteModalVisible(true);
  };

  const confirmDeleteFile = () => {
    if (fileToDelete) {
      notification.info({
        message: "Deleting File",
        description: `Deleting "${fileToDelete.fileName}". This may take a moment.`,
        duration: 3,
      });
      deleteProjectFile(id, fileToDelete.fileId);
      setDeleteModalVisible(false);
      setFileToDelete(null);
    }
  };

  const cancelDeleteFile = () => {
    setDeleteModalVisible(false);
    setFileToDelete(null);
  };

  const handleDownloadFile = async (fileId, fileName) => {
    setDownloadingFileId(fileId);
    try {
      await downloadProjectFile(id, fileId, fileName);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleReindex = async (fileIds = null) => {
    setIsReindexing(true);
    try {
      const response = await reindexFiles(id, fileIds);
      if (response?.data?.filesQueued > 0) {
        notification.success({
          message: "Reindex Started",
          description: `${response.data.filesQueued} file(s) queued for reindexing`,
        });
        fetchIndexingStatus();
        getProjectFiles(id);
      } else {
        notification.info({
          message: 'No Files to Reindex',
          description: 'All files are already indexed or no failed files found',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to start reindexing',
      });
    } finally {
      setIsReindexing(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    setSettingsModalVisible(false);
    try {
      await updateProjectSettings(id, {
        system_prompt: systemPrompt,
        default_provider: defaultProvider,
      });
    } catch (error) {
      setIsSavingSettings(false);
    }
  };

  const handleSaveProjectName = async (newName) => {
    await updateProject(id, { name: newName });
    setProject((prev) => ({ ...prev, name: newName }));
  };

  const handleSaveProjectDescription = async (newDescription) => {
    await updateProject(id, { description: newDescription });
    setProject((prev) => ({ ...prev, description: newDescription }));
  };

  const handleSaveDemographicFormat = async () => {
    setIsSavingDemographicFormat(true);
    try {
      await updateProjectSettings(id, {
        demographic_format: demographicFormat,
      });
    } catch (error) {
      setIsSavingDemographicFormat(false);
    }
  };

  const handleRegenerateContext = async () => {
    setIsRegeneratingContext(true);
    try {
      const response = await regenerateProjectContext(id);
      // API returns { status: 1, statusCode: 200, message: '...', data: {...} }
      if (response?.status === 1) {
        if (response.data?.hasDataFiles) {
          notification.success({
            message: "Context Regenerated",
            description: "Project context has been updated successfully.",
          });
        } else {
          notification.info({
            message: "No Data Files",
            description: "No CSV/Excel files found in the project to generate context from.",
          });
        }
        fetchIndexingStatus();
        getProjectFiles(id);
      } else {
        notification.error({
          message: "Error",
          description: response?.message || "Failed to regenerate project context",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message || "Failed to regenerate project context",
      });
    } finally {
      setIsRegeneratingContext(false);
    }
  };

  const handleInsightEngineClick = () => {
    setNewSessionName("");
    setNewSessionProvider("");
    setNewSessionModal(true);
  };

  const handleCreateNewSession = async () => {
    if (!newSessionName.trim()) {
      notification.warning({
        message: "Session Name Required",
        description: "Please enter a name for your session",
      });
      return;
    }
    setIsCreatingChat(true);
    try {
      await createChat({
        projectId: id,
        title: newSessionName.trim(),
        aiProvider: newSessionProvider,
      });
    } catch (error) {
      setIsCreatingChat(false);
    }
  };

  const handleCancelNewSession = () => {
    if (!isCreatingChat) {
      setNewSessionModal(false);
      setNewSessionName("");
      setNewSessionProvider("");
    }
  };

  const handleOpenSession = (chatId) => {
    navigate(`/insight-engine/${chatId}`);
  };

  const handleDeleteSession = (chatId) => {
    deleteChat(chatId);
  };

  const handleUploadModalTabChange = (tab) => {
    setUploadModalTab(tab);
    // Clear selections when switching tabs, but preserve batchTag
    if (tab === 'import') {
      setSelectedFiles([]); // Clear selected files when switching to import
      getEligibleStudies(id);
    } else {
      setSelectedStudyIds([]); // Clear selected studies when switching to upload
    }
  };

  const handleStudySelect = (studyId) => {
    setSelectedStudyIds((prev) => {
      if (prev.includes(studyId)) {
        return prev.filter((id) => id !== studyId);
      }
      return [...prev, studyId];
    });
  };

  const handleImportFromStudies = async () => {
    if (selectedStudyIds.length === 0) {
      notification.warning({
        message: 'No Studies Selected',
        description: 'Please select at least one study to import from',
      });
      return;
    }

    setIsImporting(true);
    try {
      await importFromStudies(id, selectedStudyIds, batchTag.trim() || '');
    } catch (error) {
      setIsImporting(false);
    }
  };

  const getImportSummary = () => {
    const selectedStudies = eligibleStudies.filter((s) => selectedStudyIds.includes(s._id));
    const totalAvailable = selectedStudies.reduce((sum, s) => sum + (s.available_to_import || 0), 0);
    const totalAlreadyImported = selectedStudies.reduce((sum, s) => sum + (s.already_imported || 0), 0);
    return { totalAvailable, totalAlreadyImported, studyCount: selectedStudies.length };
  };

  const filteredStudies = eligibleStudies.filter((study) =>
    study.campaign_name?.toLowerCase().includes(studySearchTerm.toLowerCase())
  );

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "just now";
    const minutes = Math.floor(diffInSeconds / 60);
    if (diffInSeconds < 3600) return `${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`;
    const hours = Math.floor(diffInSeconds / 3600);
    if (diffInSeconds < 86400) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    const days = Math.floor(diffInSeconds / 86400);
    if (diffInSeconds < 604800) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    return formatDateTime(dateString);
  };

  const handleBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFilesSelected(files);
    }
    e.target.value = "";
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileType) => {
    const iconMap = {
      pdf: "/images/pdf-icon.svg",
      csv: "/images/csv-icon.svg",
      doc: "/images/doc-icon.svg",
      docx: "/images/doc-icon.svg",
      txt: "/images/txt-icon.svg",
      json: "/images/json-icon.svg",
      xls: "/images/csv-icon.svg",
      xlsx: "/images/csv-icon.svg",
    };
    return iconMap[fileType] || "/images/txt-icon.svg";
  };

  const getStatusColor = (status) => {
    const colorMap = {
      uploaded: "success",
      indexing: "processing",
      indexed: "cyan",
      pending: "warning",
      failed: "error",
    };
    return colorMap[status] || "default";
  };

  const getStatusText = (status) => {
    const textMap = {
      uploaded: "Uploaded",
      indexing: "Indexing",
      indexed: "Indexed",
      pending: "Pending",
      failed: "Failed",
    };
    return textMap[status] || status;
  };

  const getProjectStatusColor = (status) => {
    const colorMap = {
      draft: "default",
      indexing: "processing",
      ready: "success",
    };
    return colorMap[status] || "default";
  };

  const getProjectStatusText = (status) => {
    const textMap = {
      draft: "Draft",
      indexing: "Indexing...",
      ready: "Ready",
    };
    return textMap[status] || status;
  };

  const filteredFiles = files.filter((file) =>
    file.original_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Combined permission checks: role-based OR owner OR share-based permissions
  const isOwner = project?.created_by?._id === currentUserId || project?.created_by === currentUserId;
  const isSystemAdmin = currentUserType === USER_TYPES.SUPER_ADMIN;
  const isSharedToMe = isProjectSharedToMe(project);
  const sharePerms = project?.sharePermissions;

  // Permission helpers:
  // - System admins always have full access
  // - First check role-based permission (module/submodule access)
  // - Then check if owner OR (shared with share permission)
  // This ensures role permission is ALWAYS checked first, so if role doesn't have
  // a permission, even shared users with that share permission won't see the feature
  const canViewFiles = isSystemAdmin || isOwner || (isSharedToMe && hasSharePermission(sharePerms, 'can_view_files'));
  const canDownloadFiles = isSystemAdmin ||
    (can('download-files') && (isOwner || (isSharedToMe && hasSharePermission(sharePerms, 'can_download_files'))));
  const canUploadFiles = isSystemAdmin ||
    (can('upload-files') && (isOwner || (isSharedToMe && hasSharePermission(sharePerms, 'can_upload_files'))));
  const canDeleteFiles = isSystemAdmin ||
    (can('delete-files') && (isOwner || (isSharedToMe && hasSharePermission(sharePerms, 'can_delete_files'))));
  const canReindexFiles = isSystemAdmin ||
    (can('reindex-files') && (isOwner || (isSharedToMe && hasSharePermission(sharePerms, 'can_reindex_files'))));
  const canShareProject = isSystemAdmin ||
    (can('share') && (isOwner || (isSharedToMe && hasSharePermission(sharePerms, 'can_share'))));
  const canCreateChats = isSystemAdmin ||
    (can('create-chat') && (isOwner || (isSharedToMe && hasSharePermission(sharePerms, 'can_create_chats'))));
  const canViewActivity = isSystemAdmin ||
    (can('view-activity') && (isOwner || isSharedToMe));

  if (loading) {
    return (
      <>
        <MobHeader />
        <ThemeMainWrapper>
          <div className="flex justify-center items-center h-96">
            <Spin size="large" />
          </div>
        </ThemeMainWrapper>
      </>
    );
  }

  if (!project) {
    return (
      <>
        <MobHeader />
        <ThemeMainWrapper>
          <div className="text-center py-20">
            <h2>Library not found</h2>
            <ThemeButton onClick={() => navigate(getBackUrl())}>
              Back to Library
            </ThemeButton>
          </div>
        </ThemeMainWrapper>
      </>
    );
  }

  return (
    <>
      <MobHeader />
      <ThemeMainWrapper
        className="hasHeader"
        title={
          <Space gap={10}>
            <ThemeFilterBtn
              className="shadow"
              onClick={() => navigate(getBackUrl())}
              size="middle"
              icon={<img src="/images/back-icon.svg" alt="Back" />}
            />
            {isMobile ? null : (
              <EditableTitle
                value={project.name}
                onSave={handleSaveProjectName}
                placeholder="Untitled Project"
              />
            )}
            <ThemeTag
              className="ml-2"
              color={getProjectStatusColor(
                indexingStatus?.projectStatus || project.status
              )}
            >
              {indexingStatus?.projectStatus === 'indexing' && (
                <SyncOutlined spin className="mr-1" />
              )}
              {getProjectStatusText(
                indexingStatus?.projectStatus || project.status
              )}
            </ThemeTag>
          </Space>
        }
        extra={
          <Space>
            {isSuperAdmin &&
              can('update-settings') &&
              (isMobile ? (
                <ThemeFilterBtn
                  size="large"
                  shape="square"
                  icon={<SettingOutlined />}
                  onClick={() => setSettingsModalVisible(true)}
                />
              ) : (
                <ThemeFilterBtn
                  size="large"
                  shape="square"
                  icon={<SettingOutlined />}
                  onClick={() => setSettingsModalVisible(true)}
                >
                  Settings
                </ThemeFilterBtn>
              ))}
            {canShareProject && (
              <ThemeFilterBtn
                size="large"
                shape="square"
                icon={<ShareAltOutlined />}
                onClick={() => setShareModalVisible(true)}
              />
            )}
            {canCreateChats && (() => {
              const currentStatus = indexingStatus?.projectStatus || project.status;
              const isProjectReady = currentStatus === 'ready';
              const isDisabled = !isProjectReady || isCreatingChat;
              const tooltipMessage = currentStatus === 'indexing'
                ? 'Project is being processed. Please wait.'
                : currentStatus === 'draft'
                  ? 'This is a draft project. Upload files to make it ready.'
                  : '';

                const button = isMobile ? (
                  <ThemeFilterBtn
                    onClick={handleInsightEngineClick}
                    size="large"
                    loading={isCreatingChat}
                    disabled={isDisabled}
                    icon={
                      <img
                        src="/images/insights-icons.svg"
                        alt="Analyze"
                      />
                    }
                  />
                ) : (
                  <ThemeButton
                    onClick={handleInsightEngineClick}
                    size="large"
                    loading={isCreatingChat}
                    disabled={isDisabled}
                    icon={
                      <img
                        src="/images/insights-icons.svg"
                        alt="Analyze"
                      />
                    }
                  >
                    {isCreatingChat ? 'Creating...' : 'Analyze'}
                  </ThemeButton>
                );

              return !isProjectReady && tooltipMessage ? (
                <Tooltip title={tooltipMessage}>
                  <span>{button}</span>
                </Tooltip>
              ) : button;
            })()}
          </Space>
        }
      >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={24} lg={24} xl={16} xxl={18}>
              <Row gutter={[16, 16]} className="mb-4">
              {isMobile ? (
                <Col xs={24} className="mt-3">
                  <EditableTitle
                    value={project.name}
                    onSave={handleSaveProjectName}
                    placeholder="Untitled Project"
                  />
                </Col>
              ) : null}
                <Col xs={24}>
                  <ProjectDetailsCard className="compact-card">
                    <EditableDescription
                      value={project.description}
                      onSave={handleSaveProjectDescription}
                      label="Description"
                      placeholder="Add a description for this project..."
                      rows={2}
                    />
                  </ProjectDetailsCard>
                </Col>
              </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8} md={8}>
                <ProjectMainDetails className="totalFilesCard">
                  <Row className="align-items-center">
                    <Col xs={20} sm={16}>
                      <div
                        style={{
                          fontSize: 14,
                          color: '#64748b',
                          marginBottom: 6,
                        }}
                      >
                        Total Files
                      </div>
                      <div
                        style={{
                          fontSize: "clamp(24px, 1rem + 0.8vw, 66px)",
                          fontWeight: 600,
                          color: '#1e293b',
                          lineHeight: 1,
                        }}
                      >
                        {project?.files_count || 0}
                      </div>
                    </Col>
                    <Col xs={4} sm={8}>
                      <img
                        src="/images/proj-details-icon-1.svg"
                        alt="Total Files"
                      />
                    </Col>
                  </Row>
                </ProjectMainDetails>
              </Col>
              <Col xs={24} sm={8} md={8}>
                <ProjectMainDetails className="filesIndexedCard">
                  <Row className="align-items-center">
                    <Col xs={20} sm={16}>
                      <div
                        style={{
                          fontSize: 14,
                          color: '#64748b',
                          marginBottom: 6,
                        }}
                      >
                        Files Indexed
                      </div>
                      <div
                        style={{
                          fontSize: "clamp(24px, 1rem + 0.8vw, 66px)",
                          fontWeight: 600,
                          color: '#1e293b',
                          lineHeight: 1,
                        }}
                      >
                        {indexingStatus?.fileCounts?.indexed || 0}
                      </div>
                    </Col>
                    <Col xs={4} sm={8}>
                      <img
                        src="/images/proj-details-icon-2.svg"
                        alt="Total Files"
                      />
                    </Col>
                  </Row>
                </ProjectMainDetails>
              </Col>
              <Col xs={24} sm={8} md={8}>
                <ProjectMainDetails className="chatSessionsCard">
                  <Row className="align-items-center">
                    <Col xs={20} sm={16}>
                      <div
                        style={{
                          fontSize: 14,
                          color: '#64748b',
                          marginBottom: 6,
                        }}
                      >
                        Chat Sessions
                      </div>
                      <div
                        style={{
                          fontSize: "clamp(24px, 1rem + 0.8vw, 66px)",
                          fontWeight: 600,
                          color: '#1e293b',
                          lineHeight: 1,
                        }}
                      >
                        {chatSessions?.length || 0}
                      </div>
                    </Col>
                    <Col xs={4} sm={8}>
                      <img
                        src="/images/proj-details-icon-3.svg"
                        alt="Total Files"
                      />
                    </Col>
                  </Row>
                </ProjectMainDetails>
              </Col>
                {canViewFiles && (
              <Col xs={24} className="mb-0">
                <ProjectDetailsUploadCard
                  className="filesUploadCard"
                  title={
                    <>
                      Files <br />
                      <span>Total {project.files_count || 0} Files</span>
                    </>
                  }
                  extra={
                    <Space gap={10}>
                      <ThemeInput
                        className=""
                        size="large"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                      {indexingStatus?.fileCounts?.failed > 0 &&
                        canReindexFiles && (
                          <ThemeButton
                            className="noBackground"
                            size="large"
                            onClick={() => handleReindex()}
                            loading={isReindexing}
                            icon={<ReloadOutlined />}
                          >
                            Reindex Failed ({indexingStatus.fileCounts.failed})
                          </ThemeButton>
                        )}
                      {canUploadFiles && (
                        <>
                          {isMobile ? (
                            <ThemeFilterBtn
                              className={filteredFiles.length > 0 ? "noBackground" : undefined}
                              size="large"
                              onClick={() => setUploadModal(true)}
                              icon={
                                <img
                                  src="/images/files-uploaded-icon.svg"
                                  alt="Upload Files"
                                />
                              }
                            />
                          ) : (
                            <ThemeButton
                              className={filteredFiles.length > 0 ? "noBackground" : undefined}
                              size="large"
                              onClick={() => setUploadModal(true)}
                              icon={
                                <img
                                  src="/images/files-uploaded-icon.svg"
                                  alt="Upload Files"
                                />
                              }
                            >
                              Upload Files
                            </ThemeButton>
                          )}
                        </>
                      )}
                    </Space>
                  }
                >
                  {indexingStatus?.projectStatus === 'indexing' && (
                    <div className="mt-4 mb-4 px-4">
                      <div className="flex justify-between text-sm text-gray-500 mb-1">
                        <span>Indexing Progress</span>
                        <span>
                          {indexingStatus.fileCounts?.indexed || 0} /{' '}
                          {indexingStatus.totalFiles || 0} files
                        </span>
                      </div>
                      <Progress
                        percent={indexingStatus.progress || 0}
                        status="active"
                        strokeColor={{ from: '#108ee9', to: '#87d068' }}
                        size="small"
                      />
                    </div>
                  )}
                  <ThemeTable
                    className="noBorders mt-1"
                    scroll={{ x: 768 }}
                    showHeader={false}
                    loading={getProjectFilesState.apiState === 'loading'}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    // locale={{
                    //   emptyText:
                    //     "No files uploaded yet. Click 'Upload Files' to add files.",
                    // }}

                    locale={{
                      emptyText:(<div className="text-center text-sm text-gray-500">
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No files uploaded yet. Click 'Upload Files' to add files." />
                      </div>),
                    }}  
                    columns={[
                      {
                        dataIndex: 'icons',
                        align: 'center',
                        width: '80px',
                      },
                      {
                        dataIndex: 'fileName',
                      },
                      {
                        dataIndex: 'actions',
                        align: 'center',
                      },
                    ]}
                    dataSource={filteredFiles.map(file => ({
                      key: file._id,
                      icons: (
                        <img
                          className="m-auto"
                          src={getFileIcon(
                            file.file_extension || file.file_type
                          )}
                          alt="file"
                        />
                      ),
                      fileName: (
                        <div className="fileName">
                          {file.original_name} <br />
                          <div className="smallText">
                            {file.file_size_formatted}
                            <Badge className="mx-2" status="default" />
                            {formatTimeAgo(file.createdAt)}
                          </div>
                          <ThemeTag
                            className="ml-3 py-0 px-2 text-xs"
                            color={getStatusColor(file.status)}
                          >
                            {getStatusText(file.status)}
                          </ThemeTag>
                        </div>
                      ),
                      actions: (
                        <Space>
                          {file.status === 'failed' && canReindexFiles && (
                            <ThemeFilterBtn
                              type="text"
                              shape="circle"
                              size="large"
                              icon={<ReloadOutlined />}
                              onClick={() => handleReindex([file._id])}
                              title="Reindex file"
                            />
                          )}
                          {canDownloadFiles && (
                            <ThemeFilterBtn
                              type="text"
                              shape="circle"
                              size="large"
                              disabled={downloadingFileId === file._id}
                              icon={
                                downloadingFileId === file._id ? (
                                  <LoadingOutlined spin />
                                ) : (
                                  <img
                                    src="/images/download-icon.svg"
                                    alt="Download"
                                  />
                                )
                              }
                              onClick={() =>
                                handleDownloadFile(file._id, file.original_name)
                              }
                            />
                          )}
                          {canDeleteFiles && (
                            <ThemeFilterBtn
                              type="text"
                              shape="circle"
                              size="large"
                              icon={
                                <img
                                  src="/images/delete-icon.svg"
                                  alt="Delete"
                                />
                              }
                              onClick={() =>
                                handleDeleteFile(file._id, file.original_name)
                              }
                            />
                          )}
                        </Space>
                      ),
                    }))}
                  />
                </ProjectDetailsUploadCard>
                <ThemeModal
                  title={
                    <>
                      Add Files <br />
                      <span className="uploadDocumentSubTitle">
                        Upload documents or import from studies
                      </span>
                    </>
                  }
                  style={{ top: 20 }}
                  open={uploadModal}
                  closable={!isUploading && !isImporting}
                  maskClosable={!isUploading && !isImporting}
                  onCancel={() => {
                    if (!isUploading && !isImporting) {
                      setUploadModal(false);
                      setSelectedFiles([]);
                      setSelectedStudyIds([]);
                      setBatchTag('');
                      setUploadModalTab('upload');
                      setStudySearchTerm('');
                    }
                  }}
                  centered
                  footer={
                    uploadModalTab === 'upload' ? (
                      selectedFiles.length > 0 && (
                        <div className="text-center py-2">
                          <ThemeButton
                            size="large"
                            onClick={handleUploadFiles}
                            disabled={isUploading}
                            loading={isUploading}
                          >
                            {isUploading
                              ? `Uploading ${bulkUploadState.progress?.completed || 0}/${bulkUploadState.progress?.total || selectedFiles.length}`
                              : `Upload ${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}`}
                          </ThemeButton>
                        </div>
                      )
                    ) : (
                      selectedStudyIds.length > 0 && (
                        <div className="text-center py-2">
                          <ThemeButton
                            size="large"
                            onClick={handleImportFromStudies}
                            disabled={isImporting || getImportSummary().totalAvailable === 0}
                            loading={isImporting}
                          >
                            {isImporting
                              ? 'Importing...'
                              : `Import ${getImportSummary().totalAvailable} Transcript${getImportSummary().totalAvailable !== 1 ? 's' : ''}`}
                          </ThemeButton>
                        </div>
                      )
                    )
                  }
                >
                  {/* Tab Toggle */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <ThemeButton
                      size="middle"
                      className={uploadModalTab === 'upload' ? '' : 'noBackground'}
                      onClick={() => handleUploadModalTabChange('upload')}
                      disabled={isUploading || isImporting}
                    >
                      Upload Files
                    </ThemeButton>
                    <ThemeButton
                      size="middle"
                      className={uploadModalTab === 'import' ? '' : 'noBackground'}
                      onClick={() => handleUploadModalTabChange('import')}
                      disabled={isUploading || isImporting}
                    >
                      Import from Study
                    </ThemeButton>
                  </div>

                  {uploadModalTab === 'upload' ? (
                    <>
                  {isUploading && bulkUploadState.progress?.percentage > 0 && (
                    <div className="mb-4">
                      <Progress
                        percent={bulkUploadState.progress?.percentage || 0}
                        status="active"
                        strokeColor={{ from: '#108ee9', to: '#87d068' }}
                      />
                      <div className="text-center text-sm text-gray-500">
                        Uploading {bulkUploadState.progress?.completed || 0} of{' '}
                        {bulkUploadState.progress?.total ||
                          selectedFiles.length}{' '}
                        files
                      </div>
                    </div>
                  )}
                  <style>
                    {`
                        .project-upload-area .ant-upload-drag,
                        .project-upload-area .ant-upload,
                        .project-upload-area .ant-upload-btn {
                          cursor: default !important;
                        }
                      `}
                    </style>
                    <div className="project-upload-area">
                      <ThemeUploadDragDrop
                        openFileDialogOnClick={false}
                        beforeUpload={(file) => {
                          // Validate then add to selectedFiles - don't auto upload
                          handleFilesSelected([file]);
                          return false;
                        }}
                        showUploadList={false}
                        multiple={true}
                        disabled={isUploading}
                        accept=".txt,.csv,.pdf,.doc,.docx,.xls,.xlsx,.json"
                      >
                        <div className="iconWrapper">
                          {isUploading ? (
                            <div style={{ width: 80, height: 80, margin: '0 auto' }}>
                              <Lottie animationData={loaderJson} loop={true} />
                            </div>
                          ) : (
                            <img className="m-auto" src="/images/drag-icon.svg" alt="Upload" />
                          )}
                        </div>
                        <div className="uploadTitle mt-4">
                          {isUploading ? "Uploading..." : "Drag your file(s) to start uploading"}
                        </div>
                        <ThemeDivider>Or</ThemeDivider>
                        <ThemeButton
                          size="middle"
                          shape="round"
                          className="btnBorder noBackground"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBrowseFiles();
                          }}
                          disabled={isUploading}
                          style={{ cursor: 'pointer' }}
                        >
                          Browse files
                        </ThemeButton>
                      </ThemeUploadDragDrop>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      accept=".txt,.csv,.pdf,.doc,.docx,.xls,.xlsx,.json"
                      onChange={handleFileInputChange}
                    />
                    <div className="uploadTitle my-2">
                      Only support TXT, CSV, PDF, DOC, DOCX, XLS, XLSX, JSON (Max 100MB each)
                    </div>
                    {selectedFiles.length > 0 && !isUploading && (
                      <div className="mt-4">
                        <div className="text-sm font-medium mb-2">
                          Selected Files ({selectedFiles.length})
                        </div>
                        <div
                          style={{
                            maxHeight: "200px",
                            overflowY: "auto",
                            border: "1px solid #e8e8e8",
                            borderRadius: "8px",
                          }}
                        >
                          {selectedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 border-b last:border-b-0"
                              style={{ borderBottom: "1px solid #f0f0f0" }}
                            >
                              <div className="flex items-center gap-2">
                                <img
                                  src={getFileIcon(file.name.split(".").pop())}
                                  alt="file"
                                  style={{ width: 24, height: 24 }}
                                />
                                <div>
                                  <div className="text-sm truncate" style={{ maxWidth: "250px" }}>
                                    {file.name}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {formatBytes(file.size)}
                                  </div>
                                </div>
                              </div>
                              <ThemeFilterBtn
                                type="text"
                                size="small"
                                icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />}
                                onClick={() => handleRemoveSelectedFile(index)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </>
                  ) : (
                    /* Import from Study Tab */
                    <div>
                      <ThemeInput
                        size="large"
                        placeholder="Search studies..."
                        value={studySearchTerm}
                        onChange={(e) => setStudySearchTerm(e.target.value)}
                        style={{ marginBottom: 16 }}
                      />

                      {getEligibleStudiesState.apiState === 'loading' ? (
                        <div className="text-center py-8">
                          <Spin size="small" />
                          <div className="text-sm text-gray-500 mt-2">Loading studies...</div>
                        </div>
                      ) : filteredStudies.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          {eligibleStudies.length === 0
                            ? 'No studies with transcripts found'
                            : 'No studies match your search'}
                        </div>
                      ) : (
                        <div
                          style={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #e8e8e8',
                            borderRadius: '8px',
                          }}
                        >
                          {filteredStudies.map((study) => {
                            const isSelected = selectedStudyIds.includes(study._id);
                            const isDisabled = study.available_to_import === 0;
                            return (
                              <div
                                key={study._id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '10px 16px',
                                  borderBottom: '1px solid rgba(10, 27, 57, 0.05)',
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                  backgroundColor: isSelected
                                    ? 'rgba(61, 41, 105, 0.08)'
                                    : 'transparent',
                                  opacity: isDisabled ? 0.5 : 1,
                                  transition: 'background 0.15s ease',
                                }}
                                onClick={() => !isDisabled && handleStudySelect(study._id)}
                                onMouseEnter={(e) => {
                                  if (!isDisabled && !isSelected) {
                                    e.currentTarget.style.backgroundColor = 'rgba(61, 41, 105, 0.03)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <div
                                    style={{
                                      width: 16,
                                      height: 16,
                                      border: isSelected ? '1px solid #3d2969' : '1px solid rgba(10, 27, 57, 0.2)',
                                      borderRadius: 3,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                      backgroundColor: isSelected ? '#3d2969' : 'transparent',
                                    }}
                                  >
                                    {isSelected && <CheckOutlined style={{ color: 'white', fontSize: 10 }} />}
                                  </div>
                                  <div>
                                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 13, fontWeight: 500, color: '#0a1b39' }}>
                                      {study.campaign_name}
                                    </div>
                                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 12, color: '#485066', marginTop: 2 }}>
                                      {study.available_to_import} available
                                      {study.already_imported > 0 && (
                                        <span style={{ color: '#94a3b8' }}>
                                          {' '}({study.already_imported} already imported)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <ThemeTag
                                  color={study.status === 'completed' ? 'success' : 'default'}
                                >
                                  {study.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                </ThemeTag>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {selectedStudyIds.length > 0 && (
                        <div
                          style={{
                            marginTop: 16,
                            padding: 12,
                            backgroundColor: '#f8f7fa',
                            borderRadius: 8,
                          }}
                        >
                          <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 13, fontWeight: 500, color: '#0a1b39', marginBottom: 4 }}>
                            {getImportSummary().studyCount} {getImportSummary().studyCount === 1 ? 'study' : 'studies'} selected
                          </div>
                          <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 13, color: '#485066' }}>
                            {getImportSummary().totalAvailable} {getImportSummary().totalAvailable === 1 ? 'transcript' : 'transcripts'} will be imported
                          </div>
                          {getImportSummary().totalAlreadyImported > 0 && (
                            <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                              Note: {getImportSummary().totalAlreadyImported} {getImportSummary().totalAlreadyImported === 1 ? 'transcript has' : 'transcripts have'} already been imported and will be skipped.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Batch Tag Input - visible in both tabs */}
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 13, fontWeight: 500, color: '#0a1b39', marginBottom: 8 }}>
                      Tag <span style={{ color: 'rgba(31,36,48,0.4)', fontWeight: 400 }}>(optional)</span>
                    </div>
                    <ThemeInput
                      size="large"
                      placeholder="Enter a tag for this batch (e.g., Q1 Research, Wave 2)"
                      value={batchTag}
                      onChange={(e) => setBatchTag(e.target.value)}
                      disabled={isUploading || isImporting}
                      style={{ width: '100%' }}
                    />
                  </div>
                  </ThemeModal>
                </Col>
                )}
                {/* {isSuperAdmin && can('update-settings') && (
                  <Col xs={24}>
                    <ProjectSettingsCard
                      title={
                        <>
                          Settings <br />
                          <span>Configure AI behavior and preferences for this project</span>
                        </>
                      }
                      extra={
                        <ThemeButton
                          size="large"
                          onClick={handleSaveSettings}
                          loading={isSavingSettings}
                          disabled={isSavingSettings}
                        >
                          Save Settings
                        </ThemeButton>
                      }
                    >
                    <Row gutter={[24, 24]}>
                      <Col xs={24}>
                        <span className="settingsLabel">System Instructions</span>
                        <span className="settingsDescription">
                          Customize how the AI analyzes and responds to queries about this project's data.
                        </span>
                        <TextArea
                          value={systemPrompt}
                          onChange={(e) => setSystemPrompt(e.target.value)}
                          placeholder="Enter system instructions for the AI..."
                          rows={8}
                          style={{
                            resize: "vertical",
                            fontFamily: "inherit",
                            fontSize: "13px",
                            lineHeight: "1.6",
                          }}
                        />
                      </Col>
                      <Col xs={24}>
                        <span className="settingsLabel">Model</span>
                        <span className="settingsDescription">
                          Select the AI model to use for this library's Analyze sessions.
                        </span>
                        <Row gutter={[16, 16]}>
                          <Col xs={24} sm={12}>
                            <div
                              className={`providerOption ${defaultProvider === "openai" ? "active" : ""}`}
                              onClick={() => setDefaultProvider("openai")}
                            >
                              <Radio checked={defaultProvider === "openai"} />
                              <div className="providerIcon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M2 12h4l2-6 4 12 3-8 2 4h5" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                              <div>
                                <div className="providerName">Qualitative Analysis Engine</div>
                                <div className="providerDesc">Where voices become vision</div>
                              </div>
                            </div>
                          </Col>
                          <Col xs={24} sm={12}>
                            <div
                              className={`providerOption ${defaultProvider === "gemini" ? "active" : ""}`}
                              onClick={() => setDefaultProvider("gemini")}
                            >
                              <Radio checked={defaultProvider === "gemini"} />
                              <div className="providerIcon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="12" cy="12" r="9" stroke="#10b981" strokeWidth="2"/>
                                  <circle cx="12" cy="12" r="5" stroke="#10b981" strokeWidth="1.5"/>
                                  <circle cx="12" cy="12" r="2" fill="#10b981"/>
                                </svg>
                              </div>
                              <div>
                                <div className="providerName">Quantitative Analysis Engine</div>
                                <div className="providerDesc">See beyond the surface</div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <Col xs={24}>
                        <span className="settingsLabel">Data Context</span>
                        <span className="settingsDescription">
                          Regenerate the project context schema from CSV/Excel files. This helps the AI understand your data structure better.
                        </span>
                        <div style={{ marginTop: 12 }}>
                          <ThemeButton
                            className="noBackground"
                            size="large"
                            onClick={handleRegenerateContext}
                            loading={isRegeneratingContext}
                            disabled={isRegeneratingContext}
                            icon={<ReloadOutlined />}
                          >
                            {isRegeneratingContext ? "Regenerating..." : "Regenerate Context"}
                          </ThemeButton>
                        </div>
                      </Col>
                    </Row>
                    </ProjectSettingsCard>
                  </Col>
                )} */}
                {(isSuperAdmin || can('anonymous-demographic-switch')) && (
                  <Col xs={24}>
                    <ProjectSettingsCard
                      className="demographicCard"
                      title={
                        <>
                          Demographic Display <br />
                          <span>Control how participant demographics appear in Insight Engine responses</span>
                        </>
                      }
                      extra={
                        <ThemeButton
                          size="large"
                          onClick={handleSaveDemographicFormat}
                          loading={isSavingDemographicFormat}
                          disabled={isSavingDemographicFormat}
                        >
                          Save
                        </ThemeButton>
                      }
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24}>
                          <span className="settingsDescription" style={{ marginBottom: 16, display: 'block' }}>
                            Choose how participant demographics are displayed in AI responses. Anonymized mode uses age ranges and regions to protect participant privacy.
                          </span>
                          <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12}>
                              <div
                                className={`providerOption ${demographicFormat === "anonymized" ? "active" : ""}`}
                                onClick={() => setDemographicFormat("anonymized")}
                              >
                                <ThemeRadio checked={demographicFormat === "anonymized"} />
                                <div className="providerIcon">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" stroke="#6366f1" strokeWidth="2"/>
                                    <path d="M12 6v6l4 2" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </div>
                                <div>
                                  <div className="providerName">General</div>
                                  <div className="providerDesc">Age ranges (18-24) & regions (Northeast)</div>
                                </div>
                              </div>
                            </Col>
                            <Col xs={24} sm={12}>
                              <div
                                className={`providerOption ${demographicFormat === "detailed" ? "active" : ""}`}
                                onClick={() => setDemographicFormat("detailed")}
                              >
                                <ThemeRadio checked={demographicFormat === "detailed"} />
                                <div className="providerIcon">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M2 17l10 5 10-5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M2 12l10 5 10-5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                                <div>
                                  <div className="providerName">Detailed</div>
                                  <div className="providerDesc">Exact ages (25) & locations (Oregon)</div>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </ProjectSettingsCard>
                  </Col>
                )}
              </Row>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={8} xxl={6}>
              <ProjectChatCard
                className="chatSessionsCard"
                title={
                  <>
                    Chat Sessions <br />
                    <span>Interactive conversations with your library data</span>
                  </>
                }
              >
                {getChatsByProjectState.apiState === "loading" ? (
                  <div className="text-center py-8">
                    <Spin size="small" />
                  </div>
                ) : chatSessions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No chat sessions yet.</p>
                    <p className="text-sm">Click "Analyze" to start a new chat session.</p>
                  </div>
                ) : (
                  chatSessions.map((session) => (
                    <ProjectChatSessionsDetailsCard
                      key={session._id}
                      className="mb-3 pb-0"
                      bodyStyle={{ display: 'none' }}
                      title={
                        <Space align="center">
                        {/* <ProjectChatSessionsTag icon={<img src="/images/comment-massage.svg" alt="Messages" />}>
                          Chat Session
                        </ProjectChatSessionsTag> */}
                        <img
                          className="mr-2"
                          src="/images/comment-new-icon.svg"
                          alt="Messages"
                        />
                        <div className="data">
                          <a
                            onClick={() => handleOpenSession(session._id)}
                            style={{ cursor: 'pointer', color: 'inherit' }}
                          >
                            {session.title}
                          </a>
                          <br />
                          <div className="avatarWrapper mt-1">
                            <Space align="center">
                              <img src="/images/clock-icon.svg" alt="Time" />
                              <span>{formatTimeAgo(session.updatedAt)}</span>
                            </Space>
                          </div>
                        </div>
                      </Space>
                    }
                    extra={
                      <Space>
                        <CardDropdown
                          menu={{
                            items: [
                              {
                                key: '1',
                                label: 'Open Session',
                                icon: <RightOutlined />,
                                onClick: () => handleOpenSession(session._id),
                              },
                              {
                                type: 'divider',
                              },
                              ...(can('delete-chat')
                                ? [
                                    {
                                      key: '2',
                                      label: 'Delete Session',
                                      icon: (
                                        <img
                                          src="/images/delete-icon.svg"
                                          alt="Delete"
                                        />
                                      ),
                                      onClick: () =>
                                        handleDeleteSession(session._id),
                                    },
                                  ]
                                : []),
                            ],
                          }}
                          arrow
                          placement="bottomRight"
                        >
                          <MoreOutlined />
                        </CardDropdown>
                      </Space>
                    }
                  />
                ))
              )}
            </ProjectChatCard>
          </Col>
        </Row>
      </ThemeMainWrapper>

      {/* Settings Modal */}
      {isSuperAdmin && can('update-settings') && (
        <ThemeModal
          title={
            <Space align="center">
              <img src="/images/setting-icon.svg" alt="Settings" />
              <div>
                Settings <br />
                <span className="uploadDocumentSubTitle">
                  Configure AI behavior and preferences for this project
                </span>
              </div>
            </Space>
          }
          open={settingsModalVisible}
          onCancel={() => setSettingsModalVisible(false)}
          okText="Save Settings"
          cancelText="Cancel"
          onOk={handleSaveSettings}
          loading={isSavingSettings}
          disabled={isSavingSettings}
          okButtonProps={{
            loading: isSavingSettings,
            disabled: isSavingSettings,
          }}
          cancelButtonProps={{
            onClick: () => setSettingsModalVisible(false),
          }}
          centered
        >
          <ProjectSettingsCard>
            <Row gutter={[24, 24]}>
              <Col xs={24}>
                <span className="settingsLabel">System Instructions</span>
                <span className="settingsDescription">
                  Customize how the AI analyzes and responds to queries about
                  this project's data.
                </span>
                <TextArea
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  placeholder="Enter system instructions for the AI..."
                  rows={8}
                  style={{
                    resize: 'none',
                    fontFamily: 'inherit',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    boxShadow:
                      '0px 6px 29px rgba(89, 108, 148, 0.07), 0px 0.751293px 3.63125px rgba(89, 108, 148, 0.035)',
                  }}
                />
              </Col>
              <Col xs={24}>
                <span className="settingsLabel">Model</span>
                <span className="settingsDescription">
                  Select the AI model to use for this project's Insight Engine
                  sessions.
                </span>
                <Row gutter={[16, 16]}>
                  <Col xs={24}>
                    <div
                      className={`providerOption ${defaultProvider === 'openai' ? 'active' : ''}`}
                      onClick={() => setDefaultProvider('openai')}
                    >
                      <Space>
                        <div className="providerIcon">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M2 12h4l2-6 4 12 3-8 2 4h5"
                              stroke="#6366f1"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div>
                          <div className="providerName">
                            Qualitative Analysis Engine
                          </div>
                          <div className="providerDesc">
                            Where voices become vision
                          </div>
                        </div>
                      </Space>
                      <ThemeRadio checked={defaultProvider === 'openai'} />
                    </div>
                  </Col>
                  <Col xs={24}>
                    <div
                      className={`providerOption ${defaultProvider === 'gemini' ? 'active' : ''}`}
                      onClick={() => setDefaultProvider('gemini')}
                    >
                      <Space>
                        <div className="providerIcon">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="9"
                              stroke="#10b981"
                              strokeWidth="2"
                            />
                            <circle
                              cx="12"
                              cy="12"
                              r="5"
                              stroke="#10b981"
                              strokeWidth="1.5"
                            />
                            <circle cx="12" cy="12" r="2" fill="#10b981" />
                          </svg>
                        </div>
                        <div>
                          <div className="providerName">
                            Quantitative Analysis Engine
                          </div>
                          <div className="providerDesc">
                            See beyond the surface
                          </div>
                        </div>
                      </Space>
                      <ThemeRadio checked={defaultProvider === 'gemini'} />
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col xs={24}>
                <span className="settingsLabel">Data Context</span>
                <span className="settingsDescription">
                  Regenerate the project context schema from CSV/Excel files. This helps the AI understand your data structure better.
                </span>
                <div style={{ marginTop: 12 }}>
                  <ThemeButton
                    className="noBackground"
                    size="large"
                    onClick={handleRegenerateContext}
                    loading={isRegeneratingContext}
                    disabled={isRegeneratingContext}
                    icon={<ReloadOutlined />}
                  >
                    {isRegeneratingContext ? "Regenerating..." : "Regenerate Context"}
                  </ThemeButton>
                </div>
              </Col>
              {/* <Col xs={24}>
                <ThemeButton
                  size="large"
                  onClick={handleSaveSettings}
                  loading={isSavingSettings}
                  disabled={isSavingSettings}
                >
                  Save Settings
                </ThemeButton>
              </Col> */}
            </Row>
          </ProjectSettingsCard>
        </ThemeModal>
      )}

      {/* Delete File Modal */}
      <ThemeModal
        title="Delete File"
        open={deleteModalVisible}
        onOk={confirmDeleteFile}
        onCancel={cancelDeleteFile}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        centered
      >
        <div className="py-2">
          <p className="text-base mb-2">
            Are you sure you want to delete{" "}
            <strong>"{fileToDelete?.fileName}"</strong>?
          </p>
          <p className="text-sm text-gray-500">
            This will permanently remove the file.
            This action cannot be undone.
          </p>
        </div>
      </ThemeModal>

      <ThemeModal
        title={
          <>
            New Session <br />
            <span className="uploadDocumentSubTitle">Start a new chat session</span>
          </>
        }
        open={newSessionModal}
        onCancel={handleCancelNewSession}
        centered
        footer={
          <div className="text-center py-2">
            <ThemeButton
              size="large"
              onClick={handleCreateNewSession}
              loading={isCreatingChat}
              disabled={isCreatingChat || !newSessionName.trim()}
            >
              {isCreatingChat ? "Creating..." : "Start Session"}
            </ThemeButton>
          </div>
        }
      >
        <div className="py-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              What would you like to call this session?
            </label>
            <ThemeInput
              size="large"
              placeholder="e.g., Youth Sentiment Analysis, Q4 Insights..."
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              onPressEnter={() => {
                if (newSessionName.trim() && !isCreatingChat) {
                  handleCreateNewSession();
                }
              }}
              disabled={isCreatingChat}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Select Analysis Engine <span style={{ color: '#999', fontWeight: 'normal' }}>(Optional)</span>
            </label>
            <Select
              size="large"
              style={{ width: "100%" }}
              value={newSessionProvider || undefined}
              onChange={(value) => setNewSessionProvider(value)}
              disabled={isCreatingChat}
              placeholder="Use project default"
              allowClear
              onClear={() => setNewSessionProvider("")}
            >
              {Object.values(AI_PROVIDER_OPTIONS)
                .filter((opt) => {
                  if (opt.providerKey === 'openai') {
                    return project?.vector_stores?.openai?.status === 'active';
                  }
                  if (opt.providerKey === 'gemini') {
                    return project?.vector_stores?.gemini?.status === 'active';
                  }
                  return false;
                })
                .map((opt) => {
                  const IconComponent = opt.icon;
                  return (
                    <Select.Option key={opt.key} value={opt.key}>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <IconComponent />
                        {opt.name}
                      </span>
                    </Select.Option>
                  );
                })}
            </Select>
          </div>
        </div>
      </ThemeModal>

      <ShareModal
        open={shareModalVisible}
        onClose={() => setShareModalVisible(false)}
        entityType="project"
        entityId={id}
        entityName={project?.name}
        isSharedToMe={isSharedToMe}
      />

      {/* Activity Tab Button + Drawer - Permission gated */}
      {canViewActivity && (<>
      <div
        onClick={openHistoryDrawer}
        style={{
          position: 'fixed',
          right: 0,
          top: '30%',
          transform: 'translateY(-50%)',
          backgroundColor: '#fff',
          width: 36,
          height: 90,
          borderTopLeftRadius: 10,
          borderBottomLeftRadius: 10,
          borderTop: '1px solid rgba(61,41,105,0.2)',
          borderLeft: '1px solid rgba(61,41,105,0.2)',
          borderBottom: '1px solid rgba(61,41,105,0.2)',
          boxShadow: '-4px 4px 0px rgba(61,41,105,0.14)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
        }}
      >
        <span
          style={{
            transform: 'rotate(-90deg)',
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 14,
            fontWeight: 600,
            color: '#3d2969',
            whiteSpace: 'nowrap',
          }}
        >
          Activities
        </span>
      </div>

      <Drawer
        placement="right"
        onClose={() => setHistoryDrawerVisible(false)}
        open={historyDrawerVisible}
        width={428}
        closable={false}
        styles={{
          wrapper: {
            boxShadow: 'none',
          },
          content: {
            borderRadius: '20px 0 0 20px',
            border: '1px solid #e6e7ec',
            borderRight: 'none',
          },
          body: {
            padding: 0,
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header - fixed */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 29px 10px 31px',
          flexShrink: 0,
        }}>
          <span style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 16,
            fontWeight: 600,
            color: '#1f2430',
            lineHeight: '30px',
          }}>
            Activities
          </span>
          <div
            onClick={() => setHistoryDrawerVisible(false)}
            style={{ cursor: 'pointer', padding: 2 }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5L15 15" stroke="#1f2430" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: '#e6e7ec', flexShrink: 0 }} />

        {/* Scrollable Content */}
        <style>{`
          .activity-scroll-content::-webkit-scrollbar { width: 2px; }
          .activity-scroll-content::-webkit-scrollbar-track { background: transparent; }
          .activity-scroll-content::-webkit-scrollbar-thumb { background: #3d2969; border-radius: 2px; }
          .activity-scroll-content::-webkit-scrollbar-thumb:hover { opacity: 0.8; }
        `}</style>
        <div className="activity-scroll-content" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {isLoadingHistory ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Spin />
          </div>
        ) : projectHistory.length === 0 ? (
          <div style={{
            padding: 40,
            textAlign: 'center',
            color: 'rgba(31,36,48,0.6)',
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 14,
          }}>
            No activities available yet.
          </div>
        ) : (
          <div style={{ paddingTop: 24, paddingBottom: 20 }}>
            {projectHistory.map((dateGroup, groupIndex) => (
              <div key={groupIndex} style={{ marginBottom: 14 }}>
                {/* Date Header */}
                <div style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: '14px',
                  color: '#3d2969',
                  paddingLeft: 31,
                  marginBottom: 20,
                }}>
                  {dateGroup.date}
                </div>

                {/* Activity Cards */}
                {dateGroup.items.map((item, itemIndex) => {
                  const isImport = item.source_type === 'campaign_transcript';
                  const iconColor = isImport ? '#5DADFF' : '#00A63E';
                  const iconColorDark = isImport ? '#4A9AE6' : '#009035';
                  const fileWord = item.files_count === 1 ? 'File' : 'Files';
                  const title = `${item.files_count} ${fileWord} ${isImport ? 'Imported' : 'Uploaded'}`;

                  return (
                    <div
                      key={itemIndex}
                      style={{
                        width: 368,
                        minHeight: isImport ? 110 : undefined,
                        marginLeft: 31,
                        marginBottom: 10,
                        backgroundColor: '#fff',
                        border: '1px solid #e6e7ec',
                        borderRadius: 10,
                        boxShadow: '0px 6px 29px rgba(89,108,148,0.07), 0px 0.751px 3.631px rgba(89,108,148,0.04)',
                        boxSizing: 'border-box',
                        padding: '17px 19px 10px',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Title Row - Icon + Title + Tag */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginRight: 12 }}>
                          <path d="M8.167 1.167H3.5a1.167 1.167 0 0 0-1.167 1.166v9.334A1.167 1.167 0 0 0 3.5 12.833h7a1.167 1.167 0 0 0 1.167-1.166V4.667L8.167 1.167Z" fill={iconColor} />
                          <path d="M8.167 1.167v3.5h3.5" fill={iconColorDark} />
                          <rect x="4.2" y="6.3" width="5.2" height="1" rx="0.5" fill="white" />
                          <rect x="4.2" y="8.3" width="3.6" height="1" rx="0.5" fill="white" />
                          <rect x="4.2" y="10.3" width="4.4" height="1" rx="0.5" fill="white" />
                        </svg>
                        <span style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: 14,
                          fontWeight: 600,
                          lineHeight: '18px',
                          color: '#1f2430',
                          flexShrink: 0,
                        }}>
                          {title}
                        </span>
                        {item.tag && (
                          <EllipsisChip
                            text={item.tag}
                            icon={
                              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                <path d="M8.75 5.15L5.16 1.25H2.08C1.63 1.25 1.25 1.63 1.25 2.08V5.16L5.15 8.75C5.47 9.04 5.97 9.04 6.29 8.75L8.75 6.29C9.04 5.97 9.04 5.47 8.75 5.15Z" stroke="#3d2969" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                                <circle cx="3.12" cy="3.12" r="0.625" fill="#3d2969"/>
                              </svg>
                            }
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              height: 20,
                              marginLeft: 8,
                              padding: '0 10px',
                              borderRadius: 70,
                              background: 'linear-gradient(180deg, #ffffff 0%, #f4f5f9 100%)',
                              border: '1px solid #f3f4f7',
                              boxShadow: '0px 1px 3px rgba(25,33,61,0.1)',
                              boxSizing: 'border-box',
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 11,
                              fontWeight: 500,
                              lineHeight: '1',
                              color: '#3d2969',
                              minWidth: 0,
                              flexShrink: 1,
                            }}
                          />
                        )}
                      </div>

                      {/* Subtitle Row - Relative Time */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 11 }}>
                        <span style={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: '20px',
                          color: 'rgba(31,36,48,0.6)',
                        }}>
                          {formatTimeAgo(item.uploaded_at)}
                        </span>
                      </div>

                      {/* Badges Row - only shown for imports */}
                      {isImport && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {/* Fallback badge when no campaign breakdown available */}
                        {(!item.campaign_breakdown || item.campaign_breakdown.length === 0) && (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 20,
                            padding: '0 12px',
                            borderRadius: 70,
                            background: 'linear-gradient(180deg, #ffffff 0%, #f4f5f9 100%)',
                            border: '1px solid #f3f4f7',
                            boxShadow: '0px 1px 3px rgba(25,33,61,0.1)',
                            boxSizing: 'border-box',
                            fontFamily: "'Inter', sans-serif",
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: '1',
                            color: '#5DADFF',
                            whiteSpace: 'nowrap',
                          }}>
                            Imported from Study
                          </span>
                        )}

                        {/* Campaign Breakdown Chips - show first 2, expand on click */}
                        {item.campaign_breakdown && (() => {
                          const isExpanded = expandedBatches[item.batch_id];
                          const visible = isExpanded ? item.campaign_breakdown : item.campaign_breakdown.slice(0, 2);
                          const hiddenCount = item.campaign_breakdown.length - 2;
                          const campaignChipStyle = {
                            display: 'inline-block',
                            height: 20,
                            padding: '0 12px',
                            borderRadius: 70,
                            background: 'linear-gradient(180deg, #ffffff 0%, #f4f5f9 100%)',
                            border: '1px solid #f3f4f7',
                            boxShadow: '0px 1px 3px rgba(25,33,61,0.1)',
                            boxSizing: 'border-box',
                            fontFamily: "'Inter', sans-serif",
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: '20px',
                            color: '#5DADFF',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 280,
                          };
                          return (
                            <>
                              {visible.map((cb, cbIdx) => (
                                <EllipsisChip
                                  key={cbIdx}
                                  text={`${cb.count} ${cb.count === 1 ? 'file' : 'files'} from ${cb.campaign_name}`}
                                  style={campaignChipStyle}
                                />
                              ))}
                              {hiddenCount > 0 && !isExpanded && (
                                <span
                                  onClick={() => setExpandedBatches(prev => ({ ...prev, [item.batch_id]: true }))}
                                  style={{
                                    ...campaignChipStyle,
                                    color: '#8b9cc0',
                                    cursor: 'pointer',
                                    overflow: 'visible',
                                    maxWidth: 'none',
                                  }}
                                >
                                  {`+${hiddenCount} more`}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Load More Button */}
            {historyHasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 8px' }}>
                <span
                  onClick={() => !isLoadingMore && fetchProjectHistory(historyPage + 1)}
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#5DADFF',
                    cursor: isLoadingMore ? 'default' : 'pointer',
                    opacity: isLoadingMore ? 0.6 : 1,
                  }}
                >
                  {isLoadingMore ? 'Loading...' : 'Load More'}
                </span>
              </div>
            )}
          </div>
        )}
        </div>
      </Drawer>
      </>)}
    </>
  );
};

const mapStateToProps = (state) => ({
  getProjectByIdState: state.project.getProjectByIdState,
  getProjectFilesState: state.project.getProjectFilesState,
  uploadProjectFileState: state.project.uploadProjectFileState,
  deleteProjectFileState: state.project.deleteProjectFileState,
  bulkUploadState: state.project.bulkUploadState,
  updateProjectSettingsState: state.project.updateProjectSettingsState,
  createChatState: state.chat.createChatState,
  getChatsByProjectState: state.chat.getChatsByProjectState,
  deleteChatState: state.chat.deleteChatState,
  getEligibleStudiesState: state.project.getEligibleStudiesState,
  importFromStudiesState: state.project.importFromStudiesState,
});

const mapDispatchToProps = (dispatch) => ({
  getProjectById: (id) => dispatch(getProjectByIdApi(id)),
  getProjectByIdReset: () => dispatch(getProjectByIdReset()),
  getProjectFiles: (projectId, params) => dispatch(getProjectFilesApi(projectId, params)),
  getProjectFilesReset: () => dispatch(getProjectFilesReset()),
  uploadProjectFile: (projectId, file) => dispatch(uploadProjectFileApi(projectId, file)),
  uploadProjectFileReset: () => dispatch(uploadProjectFileReset()),
  deleteProjectFile: (projectId, fileId) =>
    dispatch(deleteProjectFileApi(projectId, fileId)),
  deleteProjectFileReset: () => dispatch(deleteProjectFileReset()),
  downloadProjectFile: (projectId, fileId, fileName) => dispatch(downloadProjectFileApi(projectId, fileId, fileName)),
  uploadFilesToCloud: (projectId, files, options) => dispatch(uploadFilesToCloud(projectId, files, options)),
  bulkUploadReset: () => dispatch(bulkUploadReset()),
  getIndexingStatus: (projectId) => dispatch(getIndexingStatusApi(projectId)),
  reindexFiles: (projectId, fileIds) => dispatch(reindexFilesApi(projectId, fileIds)),
  regenerateProjectContext: (projectId) => dispatch(regenerateProjectContextApi(projectId)),
  updateProjectSettings: (projectId, data) => dispatch(updateProjectSettingsApi(projectId, data)),
  updateProjectSettingsReset: () => dispatch(updateProjectSettingsReset()),
  updateProject: (id, data) => dispatch(updateProjectApi(id, data)),
  createChat: (data) => dispatch(createChatApi(data)),
  createChatReset: () => dispatch(createChatReset()),
  getChatsByProject: (projectId, params) => dispatch(getChatsByProjectApi(projectId, params)),
  getChatsByProjectReset: () => dispatch(getChatsByProjectReset()),
  deleteChat: (chatId) => dispatch(deleteChatApi(chatId)),
  deleteChatReset: () => dispatch(deleteChatReset()),
  getEligibleStudies: (projectId) => dispatch(getEligibleStudiesForImportApi(projectId)),
  getEligibleStudiesReset: () => dispatch(getEligibleStudiesReset()),
  importFromStudies: (projectId, studyIds, batchTag) => dispatch(importFromStudiesApi(projectId, studyIds, batchTag)),
  importFromStudiesReset: () => dispatch(importFromStudiesReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectDetails);
